import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminDeleteOnboardingTemplate,
  AdminFetchOnboardingTemplateList,
  AdminRenameOnboardingTemplate,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import type {
  DeleteOnboardingTemplatePayload,
  FetchOnboardingTemplatesPayload,
  RenameOnboardingTemplatePayload,
} from 'src/interfaces/onboardingTemplates'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchOnboardingTemplateList(
  action: AsyncAction<Partial<FetchOnboardingTemplatesPayload>>,
) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchOnboardingTemplateList.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // The endpoint answers with a raw array rather than `{ data, total }`; the normalizer
    // handles both, so this call site stays correct if it is ever wrapped.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchOnboardingTemplateList,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    const templates = normalizeApiListPayload<OnboardingTemplate>(payload)

    yield put(AdminFetchOnboardingTemplateList.success(templates))

    if (action.next) {
      action.next(null, templates)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.onboardingTemplateList.onFetchOnboardingTemplateList] error',
    })

    yield put(AdminFetchOnboardingTemplateList.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load onboarding templates'))
    }
  }
}

export function* onRenameOnboardingTemplate(
  action: AsyncAction<Partial<RenameOnboardingTemplatePayload>>,
) {
  const collectDataTemplateId = action.payload?.collectDataTemplateId
  const name = action.payload?.name

  if (!collectDataTemplateId) {
    yield put(AdminRenameOnboardingTemplate.failed(new Error('Missing collectDataTemplateId')))

    if (action.next) {
      action.next('Missing collectDataTemplateId')
    }

    return
  }

  // The action payload is partial, so a caller can reach here with no name at all. Rejecting
  // it locally keeps a `{ name: undefined }` body from reaching the endpoint.
  if (!name) {
    yield put(AdminRenameOnboardingTemplate.failed(new Error('Missing name')))

    if (action.next) {
      action.next('Missing name')
    }

    return
  }

  try {
    // The body is built explicitly rather than forwarded: the backend handler reads only
    // `name`, so nothing else must reach it and appear to have been saved.
    const { payload }: { payload: OnboardingTemplate } = yield call(
      callSecureApi,
      Endpoints.AdminRenameOnboardingTemplate,
      {
        method: 'PUT',
        params: [{ field: ':collectDataTemplateId', value: collectDataTemplateId }],
        body: { name },
      },
    )

    // The renamed template's id is stamped from the request rather than trusted off the
    // response, so the flow-editor reducer can discard a rename that resolves after a
    // different template has loaded.
    const renamed = { ...payload, id: collectDataTemplateId }

    yield put(AdminRenameOnboardingTemplate.success(renamed))

    if (action.next) {
      action.next(null, renamed)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.onboardingTemplateList.onRenameOnboardingTemplate] error',
    })

    yield put(AdminRenameOnboardingTemplate.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to rename onboarding template'))
    }
  }
}

export function* onDeleteOnboardingTemplate(
  action: AsyncAction<Partial<DeleteOnboardingTemplatePayload>>,
) {
  const collectDataTemplateId = action.payload?.collectDataTemplateId

  if (!collectDataTemplateId) {
    yield put(AdminDeleteOnboardingTemplate.failed(new Error('Missing collectDataTemplateId')))

    if (action.next) {
      action.next('Missing collectDataTemplateId')
    }

    return
  }

  try {
    // The backend answers `{}`, so the body is not read. A template still assigned to any
    // contractor is refused with a 409 whose message reaches the admin through `next` below.
    yield call(callSecureApi, Endpoints.AdminDeleteOnboardingTemplate, {
      method: 'DELETE',
      params: [{ field: ':collectDataTemplateId', value: collectDataTemplateId }],
    })

    yield put(AdminDeleteOnboardingTemplate.success(collectDataTemplateId))

    if (action.next) {
      action.next(null, collectDataTemplateId)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.onboardingTemplateList.onDeleteOnboardingTemplate] error',
    })

    yield put(AdminDeleteOnboardingTemplate.failed(error))

    if (action.next) {
      // `resolveErrorMessage` prefers the backend's message, so the "assigned to contractors"
      // conflict is shown verbatim; the fallback only covers an error carrying no message.
      action.next(resolveErrorMessage(error, 'Failed to delete onboarding template'))
    }
  }
}

export function* onboardingTemplateListSaga() {
  // A refresh, a tab switch, or a return from the editor re-triggers the fetch, so only the
  // newest run matters.
  yield takeLatest(AdminFetchOnboardingTemplateList.type.REQUEST, onFetchOnboardingTemplateList)
  // Mutations must each run.
  yield takeEvery(AdminRenameOnboardingTemplate.type.REQUEST, onRenameOnboardingTemplate)
  yield takeEvery(AdminDeleteOnboardingTemplate.type.REQUEST, onDeleteOnboardingTemplate)
}
